import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { FileText, Upload, Download, Loader2, CheckCircle, AlertTriangle, Trash2, Search, Building2 } from 'lucide-react';
import api, { pdvApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const EDITOR_ROLES = ['super_admin', 'admin', 'amministratore', 'rspp'];

export default function HaccpPage() {
  const { user } = useAuth();
  const canEdit = EDITOR_ROLES.includes(user?.role);
  const [loading, setLoading] = useState(true);
  const [pdvs, setPdvs] = useState([]);
  const [search, setSearch] = useState('');
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputs = useRef({}); // pdv_id -> input ref

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pdvApi.getAll();
      setPdvs(res.data || []);
    } catch (e) {
      toast.error('Errore caricamento Unità Locali');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePickFile = (pdvId) => {
    if (!fileInputs.current[pdvId]) return;
    fileInputs.current[pdvId].click();
  };

  const handleUpload = async (pdvId, file) => {
    if (!file) return;
    const fname = file.name.toLowerCase();
    if (!fname.endsWith('.pdf') && !fname.endsWith('.docx')) {
      toast.error('Carica un file PDF o DOCX');
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      toast.error('File troppo grande (max 30 MB)');
      return;
    }
    setUploadingId(pdvId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/pdv/${pdvId}/haccp-manual`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Manuale caricato');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore upload');
    } finally {
      setUploadingId(null);
    }
  };

  const handleDownload = (pdvId) => {
    const token = sessionStorage.getItem('token');
    window.open(`/api/pdv/${pdvId}/haccp-manual?token=${token}`, '_blank');
  };

  const handleDelete = async (pdvId) => {
    if (!window.confirm('Eliminare il Manuale HACCP di questa Unità Locale?')) return;
    try {
      await api.delete(`/pdv/${pdvId}/haccp-manual`);
      toast.success('Manuale eliminato');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  };

  const filtered = pdvs.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.name || '').toLowerCase().includes(q)
        || (p.city || '').toLowerCase().includes(q)
        || (p.company_name || '').toLowerCase().includes(q);
  });

  const totalWith = pdvs.filter(p => !!p.haccp_manual_filename).length;
  const totalWithout = pdvs.length - totalWith;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="haccp-manual-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <FileText className="w-7 h-7 text-orange-500" />
          Manuale HACCP
        </h1>
        <p className="text-gray-600 mt-1">
          Carica il Manuale di Autocontrollo HACCP per ciascuna Unità Locale (PDF o DOCX, max 30 MB).
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">UL con Manuale</p>
                <p className="text-3xl font-bold text-emerald-600">{totalWith}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">UL senza Manuale</p>
                <p className="text-3xl font-bold text-amber-600">{totalWithout}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totale UL</p>
                <p className="text-3xl font-bold">{pdvs.length}</p>
              </div>
              <Building2 className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-3">
            <span>Manuali per Unità Locale</span>
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca UL..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="haccp-search-input"
              />
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">Nessuna UL trovata.</div>
          ) : (
            <div className="divide-y">
              {filtered.map((p) => {
                const hasManual = !!p.haccp_manual_filename;
                const isUploading = uploadingId === p.id;
                return (
                  <div key={p.id} className="flex flex-col md:flex-row md:items-center justify-between gap-3 py-3" data-testid={`haccp-row-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold flex items-center gap-2 flex-wrap">
                        {p.name}
                        {hasManual ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">Manuale presente</Badge>
                        ) : (
                          <Badge variant="outline" className="border-amber-300 text-amber-700">Mancante</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {p.company_name || ''} · {p.city || ''} {p.province ? `(${p.province})` : ''}
                      </div>
                      {hasManual && (
                        <div className="text-xs text-muted-foreground mt-1">
                          📄 {p.haccp_manual_filename} · {Math.round((p.haccp_manual_size || 0) / 1024)} KB
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {hasManual && (
                        <Button size="sm" variant="outline" onClick={() => handleDownload(p.id)} data-testid={`download-${p.id}`}>
                          <Download className="w-4 h-4 mr-1" />
                          Scarica
                        </Button>
                      )}
                      {canEdit && (
                        <>
                          <input
                            type="file"
                            accept=".pdf,.docx"
                            ref={(el) => { fileInputs.current[p.id] = el; }}
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleUpload(p.id, f);
                              e.target.value = '';
                            }}
                          />
                          <Button
                            size="sm"
                            variant={hasManual ? 'outline' : 'default'}
                            onClick={() => handlePickFile(p.id)}
                            disabled={isUploading}
                            data-testid={`upload-${p.id}`}
                          >
                            {isUploading ? (
                              <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Carica...</>
                            ) : (
                              <><Upload className="w-4 h-4 mr-1" />{hasManual ? 'Sostituisci' : 'Carica'}</>
                            )}
                          </Button>
                          {hasManual && (
                            <Button size="sm" variant="outline" onClick={() => handleDelete(p.id)} title="Elimina">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
